import React, { FC, useCallback, useContext } from 'react';
import { useSettingsServiceCache } from 'common/settingsService/impl/cache/useSettingsServiceCache';
import { useDeepMemo } from '@utmost.co/utmost-design-system/src/hooks/useCompare';
import { post } from 'src/utils/rest-clients';
import { AuthContext, AuthContextOutput } from 'src/components/Authentication/AuthContext';
import { MENU_SIDE_BAR_KEY } from 'common/menu/MENU_SIDE_BAR_KEY';
import { redirectToPublicPage } from 'utils/gql/redirectToPublicPage';
import context from 'integrations/mixpanel/useMixpanel';
import { localStorage } from 'utils/localStorage';

export const AuthProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const value = useAuth();
  const contextValue = useDeepMemo(() => value, [value]);
  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

function useAuth(): AuthContextOutput {
  const featureFlagsCache = useSettingsServiceCache();
  const mixpanel = useContext(context);

  const signOut = useCallback(
    async (props?: { sessionTimeout: boolean }) => {
      try {
        await post('auth/signout', '');
        mixpanel?.reset();
        redirectToPublicPage(props?.sessionTimeout ? new URLSearchParams({ sessionTimeout: 'true' }) : undefined);
      } catch (error) {
        window.location.reload();
      } finally {
        // Save menu preferences data (collapsed/expanded)
        const menuPreference = localStorage.getItem(MENU_SIDE_BAR_KEY);
        const ffCache = featureFlagsCache.getRawCache();

        localStorage.clear();
        sessionStorage.clear();

        localStorage.setItem(MENU_SIDE_BAR_KEY, menuPreference ?? '');
        if (ffCache) {
          featureFlagsCache.setRawCache(ffCache);
        }
      }
    },
    [featureFlagsCache, mixpanel]
  );

  return {
    signOut
  };
}
